import { useCallback, useMemo, useState } from 'react'

import { Box, Typography } from '@mui/material'
import { Alert, AlertSeverityEnum, DistributedTaskLogEntryLevelEnum } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { format, fromUnixTime } from 'date-fns'
import { omit, upperFirst } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { DATE_TIME_SECONDS_FORMAT } from '@shared/utils/support'
import {
  LevelVisualizerButton,
  TypeVizualizerButton,
  AlertIdBadge,
  DescriptionVizualizer,
} from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Monitoring'

const severityToLevel = {
  [AlertSeverityEnum.Low]: DistributedTaskLogEntryLevelEnum.Info,
  [AlertSeverityEnum.Medium]: DistributedTaskLogEntryLevelEnum.Warning,
  [AlertSeverityEnum.High]: DistributedTaskLogEntryLevelEnum.Error,
}

type Keys = keyof typeof severityToLevel
type SeverityToLevelValues = (typeof severityToLevel)[Keys]

type SearchStructure = {
  severity?: AlertSeverityEnum
  type?: 'Alert'
  id?: string
  code?: string
}

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])
  const [search, setSearch] = useState<SearchStructure>({})

  const handleTypeFilter = useCallback(() => {
    if (search.type !== 'Alert') {
      setSearch({ ...search, type: 'Alert' })
    } else {
      setSearch({ ...omit(search, 'type') })
    }
  }, [search])

  const handleLevelButton = useCallback(
    (level: SeverityToLevelValues) => {
      const convertedLevel = (Object.keys(severityToLevel) as Keys[]).find((key) => severityToLevel[key] === level)

      if (search.severity !== convertedLevel) {
        setSearch({ ...search, severity: convertedLevel! })
      } else {
        setSearch({ ...omit(search, 'severity') })
      }
    },
    [search],
  )

  const handleAlertIdButton = useCallback(
    (alertId: string) => {
      if (alertId !== search.id) {
        setSearch({ ...search, id: alertId })
      } else {
        setSearch({ ...omit(search, 'id') })
      }
    },
    [search],
  )

  const handleAlertCodeButton = useCallback(
    (code: string) => {
      if (code !== search.code) {
        setSearch({ ...search, code })
      } else {
        setSearch({ ...omit(search, 'code') })
      }
    },
    [search],
  )

  const columns = useMemo<ColumnDef<Alert>[]>(
    () => [
      {
        accessorFn: (row) => ({
          type: t('domain:Workspace.AvcTask.alert', { count: 1 }),
          description: row.details,
          timestamp: row.timestamp,
          level: severityToLevel[row.severity],
          alertId: row.id,
          alertCode: row.code,
        }),
        header: 'Entry',
        meta: {
          sizing: {
            minWidth: 300,
            px: 2,
          },
        },
        cell: ({ getValue }) => {
          const { description, timestamp, level, type, alertId, alertCode } = getValue<{
            type: 'Alert'
            description: Alert['details']
            timestamp: Alert['timestamp']
            level: SeverityToLevelValues
            alertId: Alert['id']
            alertCode: Alert['code']
          }>()
          return (
            <Box sx={{ maxWidth: '100%', py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', mb: 1, gap: 2 }}>
                <Typography variant="heading-sm">
                  {format(fromUnixTime(timestamp), DATE_TIME_SECONDS_FORMAT)}
                </Typography>
                <LevelVisualizerButton
                  level={level}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleLevelButton(level)
                  }}
                  checked={!!search.severity?.length}
                >
                  <Typography variant="text-sm">{upperFirst(level.toLowerCase())}</Typography>
                </LevelVisualizerButton>
                <TypeVizualizerButton logType={type} onClick={handleTypeFilter} checked={!!search.type} />
                <AlertIdBadge alertId={alertId} onClick={handleAlertIdButton} checked={!!search.id} />
              </Box>
              <DescriptionVizualizer
                description={description}
                alertCode={alertCode}
                onCodeClick={handleAlertCodeButton}
                checked={!!search.code}
              />
            </Box>
          )
        },
      },
    ],
    [handleAlertCodeButton, handleAlertIdButton, handleLevelButton, handleTypeFilter, search, t],
  )

  return { columns, search: omit(search, 'type') }
}
